import { formatDateLocal } from "@/shared/lib/date-time";
import { formatPriceLak } from "@/shared/lib/format-price";
import { RowActions } from "@/shared/ui/RowActions";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { OrderDTO } from "../api/client";
import { formatOrderPaymentMethodLabel } from "../utils/order-retail-display";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted"
  | "accent"
  | "success"
  | "warning"
  | "info"
> = {
  pending_payment: "warning",
  confirmed: "info",
  packing: "accent",
  shipped: "info",
  completed: "success",
  return_in_transit: "info",
  returned: "warning",
  cancelled: "destructive",
};

function orderStatusBadge(status: string) {
  return (
    <Badge variant={STATUS_BADGE_VARIANTS[status] ?? "default"}>
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  web: "ເວັບ",
  retail: "ຫນ້າຮ້ານ",
  social: "Social Media",
  pos: "ຫນ້າຮ້ານ (ເກົ່າ)",
};

type OrdersTableProps = {
  isLoading: boolean;
  data: OrderDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: OrderDTO) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function OrdersTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onPaginationChange,
  onSortingChange,
}: OrdersTableProps) {
  const columns: TanstackReactTable.ColumnDef<OrderDTO>[] = [
    {
      id: "code",
      header: "ເລກທີ",
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.code}</span>
      ),
    },
    {
      id: "channel",
      header: "ຊ່ອງທາງ",
      cell: ({ row }) => (
        <Badge variant="outline" className="font-normal">
          {CHANNEL_LABELS[row.original.channel] ?? row.original.channel}
        </Badge>
      ),
    },
    {
      id: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.customerName ?? row.original.customerEmail ?? "—"}
        </span>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => orderStatusBadge(row.original.status),
    },
    {
      id: "paymentMethod",
      header: "ວິທີຊຳລະ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatOrderPaymentMethodLabel(
            row.original.paymentMethod,
            row.original.channel,
          )}
        </span>
      ),
    },
    {
      id: "shippingFee",
      header: "ຄ່າສົ່ງ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.shippingFee != null
            ? `LAK ${formatPriceLak(row.original.shippingFee)}`
            : "—"}
        </span>
      ),
    },
    {
      id: "total",
      header: "ຍອດລວມ",
      cell: ({ row }) => (
        <span className="font-medium">
          LAK {formatPriceLak(row.original.total)}
        </span>
      ),
    },
    {
      id: "createdAt",
      header: "ວັນທີ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateLocal(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="h-4 w-4" />,
              onClick: () => onView(row.original),
            },
          ]}
          maxInline={1}
        />
      ),
    },
  ];

  return (
    <DataTable<OrderDTO, unknown>
      noDataMessage="ບໍ່ພົບຄຳສັ່ງຊື້"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id ?? "", s[0]?.desc ?? false);
      }}
    />
  );
}
